import { ReactElement, useMemo, useState } from 'react';

import {
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Card,
  CardHeader,
  Grid,
  Link,
  Typography,
  CardActions,
  CardContent,
  Box,
} from '@mui/material';

import {
  downloadFileContent,
  getSelectedInstance,
  useAwsEffect,
  useDisableFeatures,
  useEntitledEmulators,
  useProductArea,
  useLocalstackStatus,
} from '@localstack/services';
import makeStyles from '@mui/styles/makeStyles';
import { Cloud as CloudIcon, GetApp as GetAppIcon, Laptop as LaptopIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

import { FeatureUnavailableBox } from '@localstack/ui';
import { EmulatorType, PlanFamily, ProductFeature } from '@localstack/types';
import { ProductArea, ExternalLink } from '@localstack/constants';

import { useAuthProvider } from '~/hooks/useAuthProvider';
import { InstanceLayout } from '~/layouts/Customer/InstanceLayout';
import { pricingRoute } from '~/util/pricing';

import { PodSelectorCard } from '../CloudPods/components';
import { ExportToCloudPodCard } from '../CloudPods/components/ExportToCloudPodCard';
import { LoadFromCloudPodCard } from '../CloudPods/components/LoadFromCloudPodCard';

const PAGE_TITLE = 'Export/Import State';

enum ToggleState {
  LOCAL = 'local',
  CLOUD = 'CLOUD',
}

const useStyles = makeStyles((theme) => ({
  activeChip: {
    border: 'none',
    backgroundColor: theme.palette.primary.contrastText,
    color: theme.palette.primary.main,
  },
  toggleButton: {
    gap: '0.75rem',
  },
}));

export const StateManager = (): ReactElement => {
  const classes = useStyles();
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const queryParamPodName = queryParams.get('cloudpod') ?? undefined;
  const queryParamPodVersion = queryParams.get('version') ?? undefined;

  const instance = getSelectedInstance();
  const clientOverrides = instance ? { endpoint: instance.endpoint } : {};
  const { running } = useLocalstackStatus(clientOverrides);
  const enableAction = useDisableFeatures({ endpoint: clientOverrides?.endpoint });

  const hasAccessToProductArea = useProductArea({ useAuthProvider });
  const hasCloudPods = hasAccessToProductArea(ProductArea.CLOUD_PODS);

  const { hasFeature } = useAuthProvider();
  const hasAccessToPods = hasFeature(ProductFeature.FEATURE_PODS);
  const hasAccessToState = hasFeature(ProductFeature.FEATURE_LOCALSTACK_PLUGIN, 'pods');
  const emulators = useEntitledEmulators({ useAuthProvider });

  const [toggleState, setToggleState] = useState<ToggleState>(
    queryParamPodName ? ToggleState.CLOUD : ToggleState.LOCAL,
  );

  const { savePod } = useAwsEffect('LocalStack', ['savePod'], { silentErrors: false, clientOverrides });
  const exportPod = async (): Promise<void> => {
    const blob = await savePod();
    downloadFileContent(blob, `${instance?.name}-state`, 'application/zip');
  };

  return (
    <InstanceLayout title={PAGE_TITLE} endpoint={clientOverrides.endpoint}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {hasCloudPods && (
              <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                <ToggleButtonGroup
                  exclusive
                  onChange={(_, value) => setToggleState(value || toggleState)}
                  value={toggleState}
                >
                  <ToggleButton value={ToggleState.LOCAL} color="primary">
                    <Box display="flex" alignItems="center" className={classes.toggleButton}>
                      <LaptopIcon />
                      <span>Local</span>
                    </Box>
                  </ToggleButton>
                  <ToggleButton value={ToggleState.CLOUD} color="primary">
                    <Box display="flex" alignItems="center" className={classes.toggleButton}>
                      <CloudIcon />
                      <span>Cloud</span>
                    </Box>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            )}
            {running &&
              toggleState === ToggleState.LOCAL &&
              (hasAccessToState ? (
                <>
                  <Grid item xs={12} md={6}>
                    <PodSelectorCard disabled={!enableAction} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Export State" />
                      <CardContent>
                        <Typography variant="body2" color="textSecondary">
                          By clicking the button below, you can export the state of the current container into a file.
                          You can later re-import this file into the running container. You could achieve the same
                          operation via the CLI with the following command{' '}
                          <code>localstack state export &lt;file-name&gt;</code>. Read more in our{' '}
                          <Link href={ExternalLink.DOCS_STATE_MGMT} target="_blank" underline="hover">
                            documentation
                          </Link>
                          .
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <div>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={exportPod}
                            disabled={!enableAction}
                            startIcon={<GetAppIcon />}
                          >
                            Export State
                          </Button>
                        </div>
                      </CardActions>
                    </Card>
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <FeatureUnavailableBox
                    icon="localState"
                    availableIn={{ family: PlanFamily.BASE_PLANS, emulators }}
                    upgradeLink={pricingRoute(emulators)}
                    docsLink={ExternalLink.DOCS_STATE_MGMT}
                    title="Local State"
                    description="State Management lets you persist the full state of your LocalStack instance—so you can restore it after restarts, share snapshots with your team, or preload services for faster testing."
                  />
                </Grid>
              ))}
            {running && toggleState === ToggleState.CLOUD && (
              <>
                {hasAccessToPods ? (
                  <>
                    <Grid item xs={12} md={6}>
                      <LoadFromCloudPodCard
                        podName={queryParamPodName}
                        version={queryParamPodVersion}
                        size="small"
                        highlighted={!!(queryParamPodName || queryParamPodVersion)}
                        selector
                        disabled={!enableAction}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ExportToCloudPodCard disable={!enableAction} />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <FeatureUnavailableBox
                      icon="cloudpods"
                      availableIn={{ family: PlanFamily.BASE_PLANS, emulators: [EmulatorType.AWS] }}
                      upgradeLink={pricingRoute([EmulatorType.AWS])}
                      docsLink={ExternalLink.DOCS_CLOUD_PODS}
                      title="Cloud Pods"
                      description="Cloud Pods are persistent and shareable state snapshots for LocalStack. Version your infrastructure, debug with your team, and automate test setups with consistent, reproducible, pre-seeded environments—locally or in CI."
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </InstanceLayout>
  );
};
